'use client';

import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import clsx from 'clsx';
import { RatingError, RatingResponse } from '@/utils/post/AddPostRating';
import { useSubmitRating } from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/useSubmitRating';

/** Label and color per star count (1 → red … 5 → green). */
const LEVELS = [
    { label: 'خیلی بد', color: '#ef4444' },
    { label: 'بد', color: '#f97316' },
    { label: 'متوسط', color: '#eab308' },
    { label: 'خوب', color: '#84cc16' },
    { label: 'عالی', color: '#22c55e' },
];

interface Label {
    text: string;
    className: string;
    style?: React.CSSProperties;
}

interface RatingStarsProps {
    currentPostId: number;
    /** Called once the rating is saved, with the post's new average / count. */
    onRating: (
        rating: { rating: number; id: number; previous: number | null } & RatingResponse,
    ) => void;
    className?: string;
}

const RatingStars: React.FC<RatingStarsProps> = ({ currentPostId, onRating, className }) => {
    const [starHovered, setStarHovered] = useState<number>(0);
    const [rated, setRated] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    /** Bumped after a successful submit: shows the «saved» line. */
    const [celebrate, setCelebrate] = useState(0);
    const [error, setError] = useState('');
    const { submit, dialog, loginOpen } = useSubmitRating(currentPostId);

    useEffect(() => {
        const storedRating = localStorage.getItem(`user_rating_${currentPostId}`);
        setError('');
        setStarHovered(0);
        setCelebrate(0);
        setRated(storedRating ? parseInt(storedRating, 10) : null);
    }, [currentPostId]);

    // Sent from the click itself (it used to run in an effect that depended on the parent's
    // onRating callback, which is recreated on every render, so a rating could be sent twice).
    // The stars show the pick at once and fall back if it could not be saved.
    const handleStarClick = (index: number) => {
        if (isSubmitting) return;
        const previous = rated;
        setRated(index);
        setStarHovered(0);
        setError('');
        setIsSubmitting(true);
        submit(index)
            .then((result) => {
                localStorage.setItem(`user_rating_${currentPostId}`, index.toString());
                onRating({ rating: index, id: currentPostId, previous, ...result });
                setCelebrate((n) => n + 1);
            })
            .catch((err) => {
                setRated(previous);
                setError(
                    err instanceof RatingError && err.status === 401
                        ? 'برای ثبت امتیاز باید وارد حساب شوید.'
                        : err?.message || 'ثبت امتیاز انجام نشد، دوباره امتحان کنید.',
                );
            })
            .finally(() => setIsSubmitting(false));
    };

    // Hover only for a mouse: on a phone a tap must not leave a «hovered» state behind.
    const hoverIn = (e: React.PointerEvent, index: number) => {
        if (e.pointerType === 'mouse') setStarHovered(index);
    };

    const shown = starHovered || rated || 0;
    const level = shown > 0 ? LEVELS[shown - 1] : null;
    const isConfirmed = rated !== null && !starHovered && celebrate > 0;

    const errorShown = !!error && !starHovered;
    const label: Label = {
        text: errorShown
            ? error
            : isSubmitting
              ? loginOpen
                  ? 'بعد از ورود ثبت می‌شود…'
                  : 'در حال ثبت امتیاز…'
              : level
                ? isConfirmed
                    ? `امتیاز ${rated} ستاره ثبت شد، ممنون`
                    : rated !== null && !starHovered
                      ? `امتیاز شما: ${rated} ستاره`
                      : level.label
                : 'روی ستاره‌ها بزنید',
        className: errorShown
            ? 'text-red-500 dark:text-red-400'
            : level
              ? 'text-[color-mix(in_srgb,var(--level)_62%,black)] dark:text-[var(--level)]'
              : 'text-gray-500 dark:text-white/60',
        style: level && !errorShown ? { ['--level' as string]: level.color } : undefined,
    };
    const labelId = `${label.text}|${label.className}`;
    const [labelShown, setLabelShown] = useState<Label>(label);
    const [labelPrev, setLabelPrev] = useState<Label | null>(null);
    const [labelTick, setLabelTick] = useState(0);
    useEffect(() => {
        if (`${labelShown.text}|${labelShown.className}` === labelId) return;
        setLabelPrev(labelShown);
        setLabelShown(label);
        setLabelTick((n) => n + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labelId]);

    return (
        <div className={'flex flex-col items-center justify-center'}>
            <div
                className='flex gap-1'
                role='radiogroup'
                aria-label='امتیاز'
                onPointerLeave={() => setStarHovered(0)}
            >
                {LEVELS.map((item, i) => {
                    const index = i + 1;
                    const filled = shown >= index;
                    return (
                        <button
                            type='button'
                            key={index}
                            role='radio'
                            aria-checked={rated === index}
                            aria-label={`${index} ستاره، ${item.label}`}
                            onPointerEnter={(e) => hoverIn(e, index)}
                            onClick={() => handleStarClick(index)}
                            disabled={isSubmitting}
                            // 44px+ touch target
                            className='relative flex min-w-11 flex-col items-center p-1 transition-transform duration-150 hover:scale-[1.08] active:scale-[0.92] motion-reduce:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-progress rounded-lg disabled:cursor-wait'
                        >
                            <Star
                                className={clsx(
                                    'transition-colors duration-150',
                                    !filled && 'stroke-gray-400 dark:stroke-white/50',
                                    className,
                                )}
                                // the color of the level being shown, no glow
                                style={
                                    filled && level
                                        ? { fill: level.color, stroke: level.color }
                                        : undefined
                                }
                                strokeWidth={1.5}
                                size={32}
                            />
                            <span
                                className={clsx(
                                    'text-xs transition-opacity',
                                    filled ? 'opacity-80' : 'opacity-40',
                                )}
                            >
                                {index}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* The label slides with CSS (old one out upward, new one in from below). motion's
                opacity animation dropped the new label to 0 for one frame when it finished. */}
            <div className='relative mt-2 h-6 w-full overflow-y-clip text-sm' aria-live='polite'>
                {labelPrev && (
                    <span
                        key={`out-${labelTick}`}
                        aria-hidden='true'
                        className={clsx(
                            'absolute inset-0 flex items-center justify-center whitespace-nowrap font-medium motion-safe:animate-out motion-safe:fade-out-0 motion-safe:slide-out-to-top-3 motion-safe:fill-mode-forwards motion-safe:duration-150 motion-reduce:hidden',
                            labelPrev.className,
                        )}
                        style={labelPrev.style}
                    >
                        {labelPrev.text}
                    </span>
                )}
                <span
                    key={`in-${labelTick}`}
                    className={clsx(
                        'absolute inset-0 flex items-center justify-center whitespace-nowrap font-medium motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-3 motion-safe:fill-mode-both motion-safe:duration-200',
                        labelShown.className,
                    )}
                    style={labelShown.style}
                >
                    {labelShown.text}
                </span>
            </div>
            {dialog}
        </div>
    );
};

export default RatingStars;
